import { trimStyleDnaField } from "@/utils/styleDna";

function isNonEmpty(value: string | undefined | null): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

export interface BuildCharacterImagePromptInput {
  readonly general_style_dna: string;
  readonly character_dna: string;
  readonly genre: string;
  readonly target_audience: string;
  /** Project field is `tone_mood`; this is the tone line in the STYLE CONTEXT block. */
  readonly tone: string;
  readonly user_description: string;
}

/**
 * Character image prompt concatenation logic.
 * The returned string is the full prompt sent to the image model (no unresolved branches).
 */
export function buildCharacterImagePrompt(input: BuildCharacterImagePromptInput): string {
  const userDescription = trimStyleDnaField(input.user_description);

  const general = trimStyleDnaField(input.general_style_dna);
  const characterDna = trimStyleDnaField(input.character_dna);
  const hasStyleDNA = general.length > 0;

  const styleBlock = hasStyleDNA
    ? ["STYLE DNA:", general, isNonEmpty(characterDna) ? `\nCHARACTER DNA:\n${characterDna}` : ""].filter(Boolean).join("\n")
    : [
        "STYLE CONTEXT:",
        `Genre: ${isNonEmpty(input.genre) ? input.genre.trim() : "n/a"}`,
        `Target audience: ${isNonEmpty(input.target_audience) ? input.target_audience.trim() : "n/a"}`,
        `Tone: ${isNonEmpty(input.tone) ? input.tone.trim() : "n/a"}`
      ].join("\n");

  const taskBlock = ["TASK:", isNonEmpty(userDescription) ? `Generate an image of ${userDescription}` : "Generate an image."].join("\n");

  const injectionRules = hasStyleDNA
    ? [
        "INJECTION RULES:",
        "- Render a single character, full body, front-facing.",
        "- Background must be a pure white seamless backdrop.",
        "- If reference images are provided, use them as visual reinforcement only.",
        "- Do not invent, modify, or override Style DNA rules."
      ].join("\n")
    : [
        "INJECTION RULES:",
        "- Render a single character, full body, front-facing.",
        "- Background must be a pure white seamless backdrop.",
        "- Reflect the context through the level of realism, rendering style, lighting, and mood."
      ].join("\n");

  return [styleBlock, "", taskBlock, "", injectionRules].join("\n");
}

export interface BuildCharacterEditImagePromptInput {
  readonly style_summary: string;
  readonly user_description: string;
}

/**
 * Character image EDIT prompt. Unlike {@link buildCharacterImagePrompt} (fresh generation), edits
 * must never inject the full Style/Character DNA system prompt - only the short Style Summary, so
 * the model's edit stays scoped to what the user asked for instead of re-deriving the character.
 */
export function buildCharacterEditImagePrompt({ style_summary, user_description }: BuildCharacterEditImagePromptInput): string {
  const summary = trimStyleDnaField(style_summary);
  const description = trimStyleDnaField(user_description);

  const styleBlock = isNonEmpty(summary) ? `STYLE SUMMARY:\n${summary}` : "";
  const taskBlock = [
    "TASK:",
    isNonEmpty(description) ? `Edit the referenced character image: ${description}` : "Edit the referenced character image."
  ].join("\n");
  const injectionRules = [
    "INJECTION RULES:",
    "- Preserve the character's identity, proportions, and every visual detail not covered by the edit instructions.",
    "- Apply only the requested changes.",
    "- Background must remain a pure white seamless backdrop.",
    "- Do not invent, modify, or override Style DNA rules."
  ].join("\n");

  return [styleBlock, taskBlock, injectionRules].filter((block) => isNonEmpty(block)).join("\n\n");
}

export interface BuildLocationEditImagePromptInput {
  readonly style_summary: string;
  readonly user_description: string;
}

/**
 * Location image EDIT prompt. Unlike {@link buildLocationImagePrompt} (fresh generation), edits
 * must never inject the full Style/Location DNA system prompt - only the short Style Summary.
 */
export function buildLocationEditImagePrompt({ style_summary, user_description }: BuildLocationEditImagePromptInput): string {
  const summary = trimStyleDnaField(style_summary);
  const description = trimStyleDnaField(user_description);

  const styleBlock = isNonEmpty(summary) ? `STYLE SUMMARY:\n${summary}` : "";
  const taskBlock = [
    "TASK:",
    isNonEmpty(description) ? `Edit the referenced location image: ${description}` : "Edit the referenced location image."
  ].join("\n");
  const injectionRules = [
    "INJECTION RULES:",
    "- Preserve the environment's identity and every visual detail not covered by the edit instructions.",
    "- Apply only the requested changes.",
    "- The image must NEVER contain any characters or humanoid figures unless explicitly stated.",
    "- Do not invent, modify, or override Style DNA rules."
  ].join("\n");

  return [styleBlock, taskBlock, injectionRules].filter((block) => isNonEmpty(block)).join("\n\n");
}

export interface BuildLocationImagePromptInput {
  readonly general_style_dna: string;
  readonly location_dna: string;
  readonly genre: string;
  readonly target_audience: string;
  readonly tone: string;
  readonly user_description: string;
}

/**
 * Location image prompt concatenation logic.
 * The final prompt sent to the image model should contain NO conditional logic.
 * This builder resolves the correct branch before sending.
 */
export function buildLocationImagePrompt({
  general_style_dna,
  location_dna,
  genre,
  target_audience,
  tone,
  user_description
}: BuildLocationImagePromptInput): string {
  const hasStyleDNA = isNonEmpty(general_style_dna);

  const styleBlock = hasStyleDNA
    ? ["STYLE DNA:", general_style_dna.trim(), isNonEmpty(location_dna) ? `\nLOCATION DNA:\n${location_dna.trim()}` : ""]
        .filter(Boolean)
        .join("\n")
    : [
        "STYLE CONTEXT:",
        `Genre: ${isNonEmpty(genre) ? genre.trim() : "n/a"}`,
        `Target audience: ${isNonEmpty(target_audience) ? target_audience.trim() : "n/a"}`,
        `Tone: ${isNonEmpty(tone) ? tone.trim() : "n/a"}`
      ].join("\n");

  const taskBlock = ["TASK:", isNonEmpty(user_description) ? `Generate an image of ${user_description.trim()}` : "Generate an image."].join(
    "\n"
  );

  const injectionRules = hasStyleDNA
    ? [
        "INJECTION RULES:",
        "- The image must NEVER contain any characters or humanoid figures unless explicitly stated.",
        "- If reference images are provided, use them as visual reinforcement only.",
        "- Do not invent, modify, or override Style DNA rules."
      ].join("\n")
    : [
        "INJECTION RULES:",
        "- The image must NEVER contain any characters or humanoid figures unless explicitly stated.",
        "- Reflect the context through the level of realism, rendering style, lighting, and mood."
      ].join("\n");

  return [styleBlock, "", taskBlock, "", injectionRules].join("\n");
}

export interface BuildImagePromptInput {
  readonly ref_image_prompt: string;
  readonly notes?: string;
  readonly style_summary?: string;
}

export function buildImagePrompt({ ref_image_prompt, notes, style_summary }: BuildImagePromptInput): string {
  const refPrompt = ref_image_prompt.trim();
  const notesText = (notes ?? "").trim();
  const styleSummary = (style_summary ?? "").trim();

  return `${refPrompt}

NOTES
${notesText}

STYLE SUMMARY
${styleSummary}


RULES
- Use the provided character and location images as the main visual source.
- Preserve character identity and proportions.
- Preserve the visual style and environment design
- The model may adjust pose, expression, framing, and camera angle to match the prompt.
- The model may show partial views of the environment as required by the composition.
- Keep lighting and atmosphere consistent with the prompt.
- Avoid distortions, duplication, or visual artifacts that are not explicitly described in the prompt.`;
}

export interface BuildCharacterModelSheetPromptInput {
  readonly style_summary?: string | null;
  readonly character_style?: string | null;
}

/**
 * Character model sheet prompt for the main-workspace “Generate Model Sheet” action.
 * Uses the Style Summary when present; falls back to Character Style DNA for style
 * documents extracted before style_summary was persisted (backwards compatibility).
 */
export function buildCharacterModelSheetPrompt(input: BuildCharacterModelSheetPromptInput): string {
  const styleSummary = trimStyleDnaField(input.style_summary);
  const characterStyle = trimStyleDnaField(input.character_style);

  const styleText = styleSummary.length > 0 ? styleSummary : characterStyle;
  const styleBlock = styleText.length > 0 ? `${styleText}\n\n` : "";

  return `A professional character model sheet of the exact referenced character on a pure solid white background. NO TEXT, NO LABELS, NO CIRCLES, NO FRAMES, NO WATERMARKS.

${styleBlock}Visually analyze the reference image and reproduce the character with exact identity preservation. Match the original silhouette, proportions, scale, facial structure, anatomy, and all defining visual features exactly as shown. This includes all species- or design-specific anatomical characteristics. Do NOT normalize, idealize, simplify, exaggerate, reinterpret, redesign, or average any aspect of the character.

The layout contains exactly EIGHT unframed items organized cleanly to optimize canvas space.

Full-Body Reference Views (4):
Generate exactly FOUR full-body views at identical scale in the character's natural neutral production reference pose appropriate for its anatomy:
1. Front view, 2. Three-quarter (3/4) view, 3. True side profile view, 4. Back view.

Expression Portraits (4):
Generate exactly FOUR floating, unframed portrait views centered on the character's face and upper anatomy (or equivalent anatomical region), representing: 1. Neutral, 2. Happy, 3. Angry, 4. Upset.

Express each emotion using the character's native expressive anatomy and visual language. Emotions should be conveyed as they naturally would for this character and its species or design—not by translating them into human facial expressions. Preserve the character's existing expressive vocabulary, facial construction, proportions, and design. Never introduce new facial deformations, expression mechanics, or anthropomorphic features that are not already supported by the reference design.

Flat, even shadowless studio lighting, crisp clean edges, highly detailed, professional studio model sheet, 8k resolution.`;
}

/**
 * Best-effort extraction of the user-authored description from a *fully built* generation prompt
 * (i.e. whatever ended up stored as `MediaEntityInfo.prompt` on the resulting image/video), for
 * read-only display/copy surfaces such as the Details tab. Handles every block shape the builders
 * above can produce:
 * - {@link buildCharacterImagePrompt} / {@link buildLocationImagePrompt}: description follows
 *   `TASK:\nGenerate an image of `, terminated by the blank line before `INJECTION RULES:`.
 * - {@link buildCharacterEditImagePrompt} / {@link buildLocationEditImagePrompt}: description
 *   follows `TASK:\nEdit the referenced character/location image: `, same termination.
 * - Style DNA test-run images (`StyleDnaPanel`): description follows a `Description:` label.
 * - {@link buildImagePrompt} / {@link buildVideoPrompt}: description is the leading text before
 *   the first `NOTES` / `STYLE SUMMARY` / `RULES` / `DIALOGUE:` block.
 * Falls back to the original text unchanged when no known block markers are found (e.g. prompts
 * from uploaded assets never built by this app).
 */
export function extractPromptDescription(stored: string | undefined | null): string {
  const text = (stored ?? "").trim();
  if (text.length === 0) {
    return "";
  }

  const taskMarker = "TASK:\n";
  const taskIndex = text.indexOf(taskMarker);
  if (taskIndex >= 0) {
    const afterTask = text.slice(taskIndex + taskMarker.length);
    const blockEnd = afterTask.indexOf("\n\n");
    const taskLine = (blockEnd >= 0 ? afterTask.slice(0, blockEnd) : afterTask).trim();
    if (/^(?:Generate an image|Edit the referenced (?:character|location) image)\.?$/i.test(taskLine)) {
      return "";
    }
    return taskLine.replace(/^(?:Generate an image of|Edit the referenced (?:character|location) image:)\s*/i, "").trim();
  }

  const descriptionMarker = "Description:\n";
  const descriptionIndex = text.indexOf(descriptionMarker);
  if (descriptionIndex >= 0) {
    const afterDescription = text.slice(descriptionIndex + descriptionMarker.length);
    const blockEnd = afterDescription.indexOf("\n\n");
    return (blockEnd >= 0 ? afterDescription.slice(0, blockEnd) : afterDescription).trim();
  }

  const markerIndexes = ["\n\nNOTES", "\n\nSTYLE SUMMARY", "\n\nRULES", "\n\nDIALOGUE:"]
    .map((marker) => text.indexOf(marker))
    .filter((index) => index >= 0);

  if (markerIndexes.length === 0) {
    return text;
  }

  return text.slice(0, Math.min(...markerIndexes)).trim();
}

export interface BuildVideoPromptInput {
  readonly video_gen_prompt: string;
  readonly style_summary?: string;
}

/** Strip the model blocks from a stored video prompt back to the user description. */
export function userVideoGenPromptFromStoredPrompt(stored: string | undefined | null): string {
  const text = (stored ?? "").trim();
  if (text.length === 0) {
    return "";
  }

  const markerIndexes = ["\n\nSTYLE SUMMARY", "\n\nRULES:", "\n\nDIALOGUE:"]
    .map((marker) => text.indexOf(marker))
    .filter((index) => index >= 0);

  if (markerIndexes.length === 0) {
    return text;
  }

  return text.slice(0, Math.min(...markerIndexes)).trim();
}

export function buildVideoPrompt({ video_gen_prompt, style_summary }: BuildVideoPromptInput): string {
  const rules = `RULES:
- Use the provided image as the main visual reference.
- Ensure motion is smooth, natural, and physically coherent.
- Avoid jitter, morphing, or identity drift.
- Maintain visual continuity throughout the shot.`;

  const body = video_gen_prompt.trim();
  const styleSummary = (style_summary ?? "").trim();
  if (styleSummary.length === 0) {
    return `${body}\n\n${rules}`;
  }

  return `${body}\n\nSTYLE SUMMARY\n${styleSummary}\n\n\n${rules}`;
}
